import React, { useState, createContext, useContext, useCallback, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, useParams } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { I18nProvider } from './hooks/useI18n';
import { ThemeProvider } from './hooks/useTheme';
import { CurrencyProvider } from './hooks/useCurrency';
import { CartProvider } from './contexts/CartContext';
import CartDrawer from './components/CartDrawer';
import useInactivityTimeout from './hooks/useInactivityTimeout';
import BugReportButton from './components/BugReportButton';
import AccessibilityButton from './components/AccessibilityButton';
import InfinityPreloader from './components/InfinityPreloader';
import InlineContentEditor from './components/InlineContentEditor';
import ContentEditsLoader from './components/ContentEditsLoader';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import './App.css';

// Retry wrapper for lazy imports — retries up to 3 times with delay
// Critical for users on slow/restricted networks (e.g. China behind GFW)
function lazyRetry(importFn, retries = 3) {
  return lazy(() => {
    return new Promise((resolve, reject) => {
      const attempt = (remaining) => {
        importFn()
          .then(resolve)
          .catch((err) => {
            if (remaining <= 0) {
              // Final attempt failed — reject with the error
              reject(err);
              return;
            }
            // Wait before retry (exponential backoff: 1s, 2s, 4s)
            setTimeout(() => attempt(remaining - 1), (retries - remaining + 1) * 1000);
          });
      };
      attempt(retries);
    });
  });
}

// Error Boundary — prevents "black screen" on any JS crash
class AppErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false, isChunkError: false }; }
  static getDerivedStateFromError(error) {
    const isChunk = error && (error.name === 'ChunkLoadError' || (error.message && error.message.includes('Loading chunk')));
    return { hasError: true, isChunkError: isChunk };
  }
  componentDidCatch(e, info) { console.error('App crash:', e, info); }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0d1117', color: '#e6edf3', fontFamily: 'system-ui', flexDirection: 'column', gap: 16, padding: 32 }}>
          <div style={{ fontSize: 48 }}>&#9888;&#65039;</div>
          <h1 style={{ fontSize: 24, fontWeight: 700 }}>
            {this.state.isChunkError ? 'Connection Issue' : 'Something went wrong'}
          </h1>
          <p style={{ color: '#8b949e', maxWidth: 400, textAlign: 'center' }}>
            {this.state.isChunkError
              ? 'Some page resources failed to load. This may be due to a slow network or regional restrictions. Please try again.'
              : 'The page encountered an error. Please try refreshing, or clear your browser cache.'}
          </p>
          <div style={{ display: 'flex', gap: 12 }}>
            <button onClick={() => window.location.reload()}
              style={{ padding: '10px 24px', background: '#238636', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14 }}>
              Retry
            </button>
            <button onClick={() => { const keepKeys = ['ib_admin_onboarded','ib_sidebar_collapsed','ib_sidebar_groups','ib_custom_labels','ib_hidden_tabs','admin_token','user_token']; const kept = {}; keepKeys.forEach(k => { const v = localStorage.getItem(k); if (v) kept[k] = v; }); localStorage.clear(); Object.entries(kept).forEach(([k,v]) => localStorage.setItem(k,v)); if ('caches' in window) { caches.keys().then(n => n.forEach(k => caches.delete(k))); } setTimeout(() => { window.location.href = '/'; }, 300); }}
              style={{ padding: '10px 24px', background: '#30363d', color: '#e6edf3', border: '1px solid #484f58', borderRadius: 8, cursor: 'pointer', fontSize: 14 }}>
              Clear Cache & Reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Lazy load ALL pages with retry for network resilience
const LandingPage = lazyRetry(() => import('./pages/LandingPage'));
const LandingPageAgency = lazyRetry(() => import('./pages/LandingPageAgency'));
const AuthPage = lazyRetry(() => import('./pages/AuthPage'));
const GoogleCallback = lazyRetry(() => import('./pages/GoogleCallback'));

// Lazy load secondary pages with retry
const AdminLogin = lazyRetry(() => import('./pages/AdminLogin'));
const AdminDashboard = lazyRetry(() => import('./pages/AdminDashboard'));
const Auth0Callback = lazyRetry(() => import('./pages/Auth0Callback'));
const FacebookCallback = lazyRetry(() => import('./pages/FacebookCallback'));
const UserDashboard = lazyRetry(() => import('./pages/UserDashboard'));
const SubscriptionPage = lazyRetry(() => import('./pages/SubscriptionPage'));
const PaymentResult = lazyRetry(() => import('./pages/PaymentResult'));
const AboutPage = lazyRetry(() => import('./pages/AboutPage'));
const ContactPage = lazyRetry(() => import('./pages/ContactPage'));
const PrivacyPage = lazyRetry(() => import('./pages/PrivacyPage'));
const BlogList = lazy(() => import('./pages/BlogPage').then(m => ({ default: m.BlogList })));
const BlogPost = lazy(() => import('./pages/BlogPage').then(m => ({ default: m.BlogPost })));
const DonatePage = lazyRetry(() => import('./pages/DonatePage'));
const BookingPage = lazyRetry(() => import('./pages/BookingPage'));
const TermsPage = lazyRetry(() => import('./pages/TermsPage'));
const DocumentationPage = lazyRetry(() => import('./pages/DocumentationPage'));
const WalkthroughPage = lazyRetry(() => import('./pages/WalkthroughPage'));
const SeoAuditPage = lazyRetry(() => import('./pages/SeoAuditPage'));
const ServicesPage = lazyRetry(() => import('./pages/ServicesPage'));
const ServiceDetailPage = lazyRetry(() => import('./pages/ServiceDetailPage'));
const ClientPortalPage = lazyRetry(() => import('./pages/ClientPortalPage'));
const FreeToolsPage = lazyRetry(() => import('./pages/FreeToolsPage'));
const CaseStudiesPage = lazyRetry(() => import('./pages/CaseStudiesPage'));
const MarketplacePage = lazyRetry(() => import('./pages/MarketplacePage'));
const DeveloperPage = lazyRetry(() => import('./pages/DeveloperPage'));
const HostingPage = lazyRetry(() => import('./pages/HostingPage'));
const TryFreePage = lazyRetry(() => import('./pages/TryFreePage'));
const MicroToolsPage = lazyRetry(() => import('./pages/MicroToolsPage'));
const ApiAccessPage = lazyRetry(() => import('./pages/ApiAccessPage'));
const MaintenancePlansPage = lazyRetry(() => import('./pages/MaintenancePlansPage'));
const AIConsultingPage = lazyRetry(() => import('./pages/AIConsultingPage'));
const SeasonalCampaignPage = lazyRetry(() => import('./pages/SeasonalCampaignPage'));
const AffiliatePartnershipsPage = lazyRetry(() => import('./pages/AffiliatePartnershipsPage'));
const WhiteLabelPage = lazyRetry(() => import('./pages/WhiteLabelPage'));
const PriorityQueuePage = lazyRetry(() => import('./pages/PriorityQueuePage'));
const SocialMediaPage = lazyRetry(() => import('./pages/SocialMediaPage'));
const TiptapEditorPage = lazyRetry(() => import('./pages/TiptapEditorPage'));
const TesterPage = lazyRetry(() => import('./pages/TesterPage'));
const DemoAccessPage = lazyRetry(() => import('./pages/DemoAccessPage'));
const TradingRequestPage = lazyRetry(() => import('./pages/TradingRequestPage'));
const PortfolioPage = lazyRetry(() => import('./pages/PortfolioPage'));
const MyOrdersPage = lazyRetry(() => import('./pages/MyOrdersPage'));
const CompetitorAnalysisPage = lazyRetry(() => import('./pages/CompetitorAnalysisPage'));
const HealthScorePage = lazyRetry(() => import('./pages/HealthScorePage'));
const VectorGeneratorPage = lazyRetry(() => import('./pages/VectorGeneratorPage'));
const ProjectPaywallPage = lazyRetry(() => import('./pages/ProjectPaywallPage'));
const ShowroomPage = lazyRetry(() => import('./pages/ShowroomPage'));
const LandingPagePremium = lazyRetry(() => import('./pages/LandingPagePremium'));

const LazyFallback = () => {
  const [useCustom, setUseCustom] = useState(true);
  useEffect(() => {
    // Check admin preference for preloader type
    try {
      const pref = localStorage.getItem('ib_preloader');
      if (pref === 'original') setUseCustom(false);
    } catch {}
  }, []);
  if (!useCustom) {
    return (
      <div className="min-h-screen bg-bg-primary flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-brand-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  return <InfinityPreloader />;
};

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

// GA4 Page View Tracker
function GATracker() {
  const location = useLocation();
  useEffect(() => {
    if (window.gtag) {
      window.gtag('config', process.env.REACT_APP_GA_TRACKING_ID, { page_path: location.pathname + location.search });
    }
    // Track visit to our own analytics
    const sessionId = sessionStorage.getItem('ib_session') || Date.now().toString(36) + Math.random().toString(36).slice(2);
    sessionStorage.setItem('ib_session', sessionId);
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/track/visit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        page: location.pathname,
        session_id: sessionId,
        referrer: document.referrer || '',
        screen_width: window.screen?.width || 0,
        screen_height: window.screen?.height || 0,
        language: navigator.language || '',
      }),
    }).catch(() => {});
    // Track as lead event
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/leads/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event_type: 'page_view', page: location.pathname, email: localStorage.getItem('user_email') || '', session_id: sessionId }),
    }).catch(() => {});
  }, [location]);
  return null;
}

function SessionGuard({ children, userToken, onExpire }) {
  const navigate = useNavigate();
  const handleTimeout = useCallback(() => {
    if (userToken) {
      onExpire();
      navigate('/login?expired=1');
    }
  }, [userToken, onExpire, navigate]);
  useInactivityTimeout(handleTimeout, 15 * 60 * 1000);
  return children;
}

// Referral link handler: /ref/:code → save code + redirect to register
function ReferralRedirect() {
  const { code } = useParams();
  React.useEffect(() => {
    if (code) localStorage.setItem('ib_referral_code', code);
    window.location.href = '/login?mode=register&ref=' + encodeURIComponent(code || '');
  }, [code]);
  return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin h-8 w-8 border-2 border-brand-primary border-t-transparent rounded-full" /></div>;
}

function DynamicLandingPage() {
  const [variant, setVariant] = useState(null);
  useEffect(() => {
    // Handle ?ref= referral links at root level (works regardless of active landing variant)
    const params = new URLSearchParams(window.location.search);
    const refCode = params.get('ref');
    if (refCode) {
      localStorage.setItem('ib_referral_code', refCode);
      window.location.href = '/login?mode=register&ref=' + encodeURIComponent(refCode);
      return;
    }
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin-platform/landing-page/active`)
      .then(r => r.json())
      .then(d => setVariant(d.active_page || 'original'))
      .catch(() => setVariant('original'));
  }, []);
  if (!variant) return <LazyFallback />;
  if (variant === 'agency') return <LandingPageAgency />;
  if (variant === 'premium') return <LandingPagePremium />;
  return <LandingPage />;
}

function AppRoutes({ userToken, adminToken, adminEmail }) {
  const location = useLocation();
  const [uiToggles, setUiToggles] = useState({ show_bug_report: true, show_accessibility: true, show_onsite_editor: true, show_content_edits: true });
  const [activeLanding, setActiveLanding] = useState('');

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin-platform/ui-toggles`)
      .then(r => r.json())
      .then(d => setUiToggles(d))
      .catch(() => {});
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin-platform/landing-page/active`)
      .then(r => r.json())
      .then(d => setActiveLanding(d.active_page || 'original'))
      .catch(() => {});
  }, []);

  // Hide floating buttons on Premium landing page (they live inside its burger menu)
  const isPremiumHome = location.pathname === '/' && activeLanding === 'premium';

  // CRITICAL: Detect session_id in URL hash synchronously during render (Emergent Google Auth)
  if (location.hash?.includes('session_id=')) {
    return <GoogleCallback />;
  }
  return (
    <div className="min-h-screen bg-bg-primary text-txt-primary noise-bg">
      <Suspense fallback={<LazyFallback />}>
        <Routes>
          <Route path="/" element={<DynamicLandingPage />} />
          <Route path="/landing/original" element={<LandingPage />} />
          <Route path="/landing/agency" element={<LandingPageAgency />} />
          <Route path="/landing/premium" element={<LandingPagePremium />} />
          <Route path="/ref/:code" element={<ReferralRedirect />} />
          <Route path="/login" element={userToken ? <Navigate to="/dashboard" replace /> : <AuthPage />} />
          <Route path="/auth/callback" element={<Auth0Callback />} />
          <Route path="/auth/google-callback" element={<GoogleCallback />} />
          <Route path="/auth/facebook-callback" element={<FacebookCallback />} />
          <Route path="/dashboard" element={userToken ? <UserDashboard /> : <Navigate to="/login" replace />} />
          <Route path="/subscriptions" element={<SubscriptionPage />} />
          <Route path="/subscription/success" element={<PaymentResult type="success" />} />
          <Route path="/subscription/cancel" element={<PaymentResult type="cancel" />} />
          <Route path="/payment/success" element={<PaymentResult type="success" />} />
          <Route path="/payment/cancel" element={<PaymentResult type="cancel" />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/blog" element={<BlogList />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/portfolio" element={<PortfolioPage />} />
          <Route path="/showroom" element={<ShowroomPage />} />
          <Route path="/donate" element={<DonatePage />} />
          <Route path="/booking" element={<BookingPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/docs" element={<DocumentationPage />} />
          <Route path="/walkthrough" element={<WalkthroughPage />} />
          <Route path="/walkthrough/:type" element={<WalkthroughPage />} />
          <Route path="/seo-audit" element={<SeoAuditPage />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/services/:slug" element={<ServiceDetailPage />} />
          <Route path="/client-portal" element={<ClientPortalPage />} />
          <Route path="/tools" element={<FreeToolsPage />} />
          <Route path="/free-tools" element={<Navigate to="/tools" replace />} />
          <Route path="/case-studies" element={<CaseStudiesPage />} />
          <Route path="/marketplace" element={<MarketplacePage />} />
          <Route path="/developer" element={<DeveloperPage />} />
          <Route path="/hosting" element={<HostingPage />} />
          <Route path="/try-free" element={<TryFreePage />} />
          <Route path="/micro-tools" element={<MicroToolsPage />} />
          <Route path="/developer-api" element={<ApiAccessPage />} />
          <Route path="/maintenance-plans" element={<MaintenancePlansPage />} />
          <Route path="/ai-consulting" element={<AIConsultingPage />} />
          <Route path="/seasonal-campaigns" element={<SeasonalCampaignPage />} />
          <Route path="/affiliate-partnerships" element={<AffiliatePartnershipsPage />} />
          <Route path="/white-label" element={<WhiteLabelPage />} />
          <Route path="/priority-queue" element={<PriorityQueuePage />} />
          <Route path="/social-media" element={<SocialMediaPage />} />
          <Route path="/editor" element={<TiptapEditorPage />} />
          <Route path="/become-tester" element={<TesterPage />} />
          <Route path="/demo-access/:linkId" element={<DemoAccessPage />} />
          <Route path="/trading-request" element={<TradingRequestPage />} />
          <Route path="/my-orders" element={userToken ? <MyOrdersPage /> : <Navigate to="/login" replace />} />
          <Route path="/competitor-analysis" element={<CompetitorAnalysisPage />} />
          <Route path="/health-score" element={<HealthScorePage />} />
          <Route path="/vector-generator" element={<VectorGeneratorPage />} />
          <Route path="/project/:linkId" element={<ProjectPaywallPage />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/*" element={adminToken ? <AdminDashboard /> : <Navigate to="/admin/login" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
      {uiToggles.show_bug_report && !isPremiumHome && <BugReportButton />}
      {uiToggles.show_accessibility && !isPremiumHome && <AccessibilityButton />}
      {uiToggles.show_content_edits !== false && <ContentEditsLoader />}
      {uiToggles.show_onsite_editor !== false && adminToken && adminEmail === 'sovic.srecko@gmail.com' && <InlineContentEditor />}
    </div>
  );
}

function App() {
  const [adminToken, setAdminToken] = useState(localStorage.getItem('admin_token'));
  const [adminEmail, setAdminEmail] = useState(localStorage.getItem('admin_email'));
  const [userToken, setUserToken] = useState(localStorage.getItem('user_token'));
  const [userEmail, setUserEmail] = useState(localStorage.getItem('user_email'));
  const [userName, setUserName] = useState(localStorage.getItem('user_name'));

  const login = (token, email, role) => {
    localStorage.setItem('admin_token', token);
    localStorage.setItem('admin_email', email);
    localStorage.setItem('admin_role', role || 'admin');
    setAdminToken(token);
    setAdminEmail(email);
  };
  const logout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_email');
    localStorage.removeItem('admin_role');
    setAdminToken(null);
    setAdminEmail(null);
  };
  const userLogin = (token, email, name) => {
    localStorage.setItem('user_token', token);
    localStorage.setItem('user_email', email);
    localStorage.setItem('user_name', name || '');
    setUserToken(token);
    setUserEmail(email);
    setUserName(name || '');
  };
  const userLogout = useCallback(() => {
    localStorage.removeItem('user_token');
    localStorage.removeItem('user_email');
    localStorage.removeItem('user_name');
    setUserToken(null);
    setUserEmail(null);
    setUserName(null);
  }, []);

  return (
    <AppErrorBoundary>
    <HelmetProvider>
      <ThemeProvider>
      <I18nProvider>
      <CurrencyProvider>
        <CartProvider>
        <AuthContext.Provider value={{
          adminToken, adminEmail, login, logout,
          userToken, userEmail, userName, userLogin, userLogout
        }}>
          <BrowserRouter>
            <GATracker />
            <SessionGuard userToken={userToken} onExpire={userLogout}>
              <AppRoutes userToken={userToken} adminToken={adminToken} adminEmail={adminEmail} />
              <CartDrawer />
              <PWAInstallPrompt />
            </SessionGuard>
          </BrowserRouter>
        </AuthContext.Provider>
        </CartProvider>
      </CurrencyProvider>
      </I18nProvider>
      </ThemeProvider>
    </HelmetProvider>
    </AppErrorBoundary>
  );
}

export default App;
